import { ENTITY_STATUS } from '@monorepo/shared/schemas/common'
import { Edit, MoreHorizontal, Pause, Play, Trash2 } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import type { Agent } from '../types/agent.types'

interface AgentCardProps {
  agent: Agent
  onView?: (agent: Agent) => void
  onAction?: (action: string, agent: Agent) => void
}

export function AgentCard({ agent, onView, onAction }: AgentCardProps) {
  return (
    <Card
      className='hover:shadow-lg transition-shadow cursor-pointer group'
      onClick={() => onView?.(agent)}
    >
      <CardHeader className='pb-4'>
        <div className='flex justify-between items-start mb-2'>
          <Badge
            variant={
              agent.status === ENTITY_STATUS.active ? 'default' : 'secondary'
            }
            className='text-xs'
          >
            {agent.status}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='sm'
                className='opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0'
                onClick={e => e.stopPropagation()}
              >
                <MoreHorizontal className='w-4 h-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  onAction?.('edit', agent)
                }}
              >
                <Edit className='w-4 h-4 mr-2' />
                Edit
              </DropdownMenuItem>
              {agent.status === ENTITY_STATUS.draft && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    onAction?.('activate', agent)
                  }}
                >
                  <Play className='w-4 h-4 mr-2' />
                  Activate
                </DropdownMenuItem>
              )}
              {agent.status === ENTITY_STATUS.active && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    onAction?.('deactivate', agent)
                  }}
                >
                  <Pause className='w-4 h-4 mr-2' />
                  Make Draft
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  onAction?.('delete', agent)
                }}
                className='text-red-600 focus:text-red-600'
              >
                <Trash2 className='w-4 h-4 mr-2' />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardTitle className='text-lg font-semibold leading-tight'>
          {agent.name}
        </CardTitle>
        {agent.external_id && (
          <p className='text-xs text-muted-foreground font-mono mt-1'>
            {agent.external_id}
          </p>
        )}
      </CardHeader>

      <CardContent className='space-y-4'>
        <p className='text-sm text-muted-foreground line-clamp-2 leading-relaxed'>
          {agent.description}
        </p>

        {/* Primary metrics */}
        <div className='grid grid-cols-2 gap-4 py-4 border-t'>
          <div className='text-center'>
            <div className='text-xl font-semibold text-green-600'>
              {agent.billableSignalsCount || 0}
            </div>
            <div className='text-xs text-muted-foreground'>
              Billable Signals
            </div>
          </div>
          <div className='text-center'>
            <div className='text-xl font-semibold text-red-600'>
              {agent.costSignalsCount || 0}
            </div>
            <div className='text-xs text-muted-foreground'>Cost Signals</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
